import { useCallback, useEffect, useRef, useState } from 'react';

import { useRutilusRestQuery } from '../../api/rest/useRutilusRestQuery';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { useGeolocation } from '@domain/geolocation/GeolocationContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { fetchMapContextSearchResults } from '@domain/search/search';
import { EventMykissSearchFocused } from '@generated/events';
import { IMapContextSearchResult } from '@app-types/search';
import { useExplore } from '../../views/Explore/context/ExploreContext';
import { CloseIcon } from '../Icons/CloseIcon';
import { SearchIcon } from '../Icons/SearchIcon';

import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { SearchOptionsMapContext } from './components/SearchOptionsMapContext';

interface IProps {
  placeholder: string;
  parent: EventMykissSearchFocused['properties']['source'];
  className?: string;
  isActive: boolean;
  isMobile?: boolean;
  setIsActive: (isActive: boolean) => void;
}

export const SearchBoxMapContext = ({
  placeholder,
  parent,
  className = '',
  isMobile = false,
  isActive,
  setIsActive,
}: IProps) => {
  const { location } = useGeolocation();
  const userPosition = location?.coordinates;
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showResults, setShowResults] = useState(false);
  const initialRender = useRef(true);
  const { saveParamsInUrl, exploreQueryParams } = useExplore();
  const [searchInputValue, setSearchInputValue] = useState<string>(exploreQueryParams.search ?? '');
  const { currentPage } = useAnalytics();

  const { isLoading: isFetching, data: results } = useRutilusRestQuery(
    async (variables, getToken, signal): Promise<unknown> => {
      return fetchMapContextSearchResults(
        getToken,
        variables.searchTerm,
        signal,
        variables.userPosition,
      );
    },
    {
      searchTerm: searchInputValue,
      userPosition,
    },
    { callOnServer: true, skip: searchInputValue === '' },
  );

  const onClickOutside = useCallback(() => {
    setShowResults(false);
    setIsActive(false);
    if (isActive) {
      trackEvent({
        name: 'mykiss_search_blurred',
        properties: {
          page: currentPage,
          search_term: searchInputValue,
          source: parent,
        },
      });
    }
  }, [setIsActive, parent, searchInputValue, isActive, currentPage]);

  useOnClickOutside(containerRef, onClickOutside);

  // Sets the search box closed on initial render
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (searchInputValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- FIB-49117
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [searchInputValue]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- FIB-49117
    setSearchInputValue(exploreQueryParams.search ?? '');
  }, [exploreQueryParams.search]);

  useEffect(() => {
    // always set focus when opened on mobile
    if (isActive && isMobile) {
      searchInputRef.current?.focus();
    }
  }, [isActive, isMobile]);

  const bottomCornerStyle = showResults ? '' : 'md:rounded-b-xl';
  return (
    <div ref={containerRef}>
      <div className="text-midnight focus-within:text-primary relative flex">
        <label
          htmlFor="SearchBoxMapContext"
          className="absolute inset-y-0 flex cursor-text items-center pl-3"
        >
          {!isActive && (
            <div className="text-midnight">
              <SearchIcon height={20} width={20} />
            </div>
          )}
        </label>
        <input
          ref={searchInputRef}
          className={`h-[56px] p-4 md:h-[44px] ${
            isActive ? 'px-6 md:px-4' : 'px-14 md:px-10'
          } text-midnight w-screen rounded-none bg-white md:w-[430px] md:rounded-t-xl ${bottomCornerStyle} ${className}`}
          type="text"
          autoComplete="off"
          id="SearchBoxMapContext"
          data-testid="SearchBoxMapContext"
          aria-label="Search"
          placeholder={placeholder}
          value={searchInputValue}
          onChange={event => {
            setSearchInputValue(event.target.value);
          }}
          onFocus={() => {
            setIsActive(true);
            if (searchInputValue) {
              setShowResults(true);
            }

            trackEvent({
              name: 'mykiss_search_focused',
              properties: {
                source: parent,
                page: currentPage,
              },
            });
          }}
        />

        <span className="absolute inset-y-0 right-2 flex cursor-pointer items-center">
          {(isMobile || !!searchInputValue) && (
            <button
              type="button"
              data-testid="map-search-close-icon"
              onClick={() => {
                setIsActive(false);
                trackEvent({
                  name: 'mykiss_search_blurred',
                  properties: {
                    page: currentPage,
                    search_term: searchInputValue,
                    source: parent,
                  },
                });
                saveParamsInUrl({ search: undefined });
              }}
              className="mr-1"
            >
              <CloseIcon />
            </button>
          )}
        </span>
      </div>
      {showResults && (
        <div>
          <div
            id="search-results-card"
            className="absolute w-screen overflow-y-auto bg-white md:w-[430px] md:rounded-b-xl md:shadow-lg"
          >
            <SearchOptionsMapContext
              isFetching={isFetching}
              results={results as IMapContextSearchResult}
              setIsActive={setIsActive}
              trackSelection={(
                name: string,
                type: 'fishing_water' | 'places',
                waterId?: string,
              ) => {
                trackEvent({
                  name: 'mykiss_search_result_selected',
                  properties: {
                    page: currentPage,
                    source: parent,
                    item_id: waterId ?? '',
                    search_term: searchInputValue,
                    item_type: type,
                    item_name: name,
                  },
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
